/**
 * Describes a function which can be used as a listener callback to a TypedEvent.
 */
export interface Listener<T> {
    (event: T): unknown;
}

/**
 * "Receipt" type returned after subscribing a listener callback to a TypedEvent.
 */
export interface Disposable {
    /**
     * Call the dispose method to unsubscribe the listener callback.
     */
    dispose: () => void;
}

/**
 * TypedEvent implements a simple, type-safe event system for pub/sub of custom
 * application events.
 */
export class TypedEvent<T = void> {
    private readonly listeners: Listener<T>[] = [];
    private listenersOncer: Listener<T>[] = [];

    /**
     * Call `on` with a callback function to subscribe to the event.
     *
     * Each time the event fires, the callback will be run. Listener callbacks
     * are run in the order in which they subscribed. Returns an object
     * containing with a `dispose` function. Call this function to remove
     * unsubscribe the listener from the event.
     */
    public readonly on = (listener: Listener<T>): Disposable => {
        this.listeners.push(listener);
        return {
            dispose: () => {
                this.off(listener);
            },
        };
    };

    /**
     * Same as `on`, but the given listener callback is run at-most once. After
     * it's been triggered a single time, the listener is automatically
     * unsubscribed from future events.
     */
    public readonly once = (listener: Listener<T>): void => {
        this.listenersOncer.push(listener);
    };

    /**
     * Unsubscribes the given listener from the event. If the listener function
     * wasn't already subscribed, this method does nothing.
     */
    public readonly off = (listener: Listener<T>): void => {
        const callbackIndex = this.listeners.indexOf(listener);
        if (callbackIndex > -1) {
            this.listeners.splice(callbackIndex, 1);
        }
    };

    /**
     * Trigger the event. The given event object is sent to each subscribed
     * listener function as its first parameter. Listener callbacks are run in
     * the order in which they subscribed.
     */
    public readonly emit = (event: T): void => {
        // Update any general listeners
        this.listeners.forEach((listener) => {
            listener(event);
        });
        // Clear the `once` queue
        if (this.listenersOncer.length > 0) {
            const toCall = this.listenersOncer;
            this.listenersOncer = [];
            toCall.forEach((listener) => {
                listener(event);
            });
        }
    };

    /**
     * Pipe this event into another event. Events must be of the same type. All
     * subsequent triggering of this event will also be emitted to the target
     * event.
     */
    public readonly pipe = (te: TypedEvent<T>): Disposable => {
        return this.on((e) => {
            te.emit(e);
        });
    };
}

/**
 * A Flag represents a boolean state value (set or unset), just like a standard
 * boolean variable. The difference is that a Flag can be subscribed to like an
 * event. Thus, you can trigger a callback to be run as soon as the Flag is set,
 * or even get a Promise object that resolves when the Flag is set (or
 * optionally, after a set timeout).
 */
export class Flag {
    private readonly event: TypedEvent<boolean>;
    private _isSet = false;

    constructor() {
        this.event = new TypedEvent<boolean>();
        this.event.on((val) => {
            this._isSet = val;
        });
    }

    /**
     * Return true is the flag is currently set.
     */
    public get isSet(): boolean {
        return this._isSet;
    }

    /**
     * Set the flag and notify all listeners of the new state.
     */
    public set(): void {
        this.event.emit(true);
    }

    /**
     * Unset the flag and notify all listeners of the new state.
     */
    public clear(): void {
        this.event.emit(false);
    }

    /**
     * Return a Promise that resolves as soon as the Flag is set. If the given
     * timeout (in milliseconds) elapses before the Flag is set, then the Promise
     * is rejected.
     */
    public async wait(timeout = 500): Promise<void> {
        // If the flag is already set, resolve immediately.
        if (this.isSet) {
            return;
        }
        // Since the flag isn't set, return a promise that resolves as soon as
        // the flag is set.
        return new Promise((resolve, reject) => {
            // Setup a timeout to reject the promise unless the flag is set
            // within the specified window.
            const timer = setTimeout(() => {
                reject(new Error("Timeout"));
            }, timeout);
            // Set an event listener to resolve the promise and clear the
            // reject-timeout as soon as the the flag gets set.
            const listener = this.event.on((isSet) => {
                if (isSet) {
                    clearTimeout(timer);
                    resolve();
                    listener.dispose();
                }
            });
        });
    }
}
